import {
    Typography,
    DialogContent,
    TextField,
    Button,
    MenuItem,
    Checkbox,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import ProficiencyClient, { ProficiencyErrors } from "../apiClients/ProficiencyClient";
import { useErrorDisplay } from "../components/ErrorDisplay"
import { GetUpsertIcon, IsEmpty } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { Proficiency } from "../models/dtos";
import ProficiencyGroupClient, { ProficiencyGroup } from "../apiClients/ProficiencyGroupClient";
import { UpsertAction } from "../Enums";
import { IfTrue } from "../Helpers";
import { I18N } from "../I18N";
import I18NText from "../components/I18NText";
import { MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

export interface ProficiencyUpsertProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    showProficiencyGroupSelect?: boolean;
    showAddToBankCheckbox?: boolean;
    addToBank?: boolean;
    proficiencyId?: string;
    inProficiency?: Proficiency;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    onCreate?: (obj: Proficiency) => void;
    onAddToBankChange?: (checked: boolean) => void;
    icon?: JSX.Element;
}

const ProficiencyUpsertPopup = ({ mode, showProficiencyGroupSelect = true, showAddToBankCheckbox = false, addToBank = false, proficiencyId, inProficiency, onCreate = (p) => { }, onChange = async () => { }, onAdd = async () => { }, onAddToBankChange = (checked) => {}, icon = GetUpsertIcon(mode), ...btnProps }: ProficiencyUpsertProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const [proficiency, setProficiency] = useState<Proficiency>(new Proficiency());
    const [proficiencyGroups, setProficiencyGroups] = useState<ProficiencyGroup[]>([]);
    const [showIsNegative, setShowIsNegative] = useState<boolean>(false);

    const disableSave = IsEmpty(proficiency?.Name);

    const title = () => {
        if (mode === UpsertAction.Create) return I18N.Translate('Create Proficiency');
        if (mode === UpsertAction.Update) return I18N.Translate('Edit Proficiency');
        return I18N.Translate('View Proficiency');
    }

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                onCreate(proficiency);
            }
            else if (mode === UpsertAction.Update) {
                let cp = {
                    Id: proficiency.Id,
                    ParentProficiencyId: null,
                    ProficiencyGroupId: proficiency.ProficiencyGroupId,
                    TemplateRevisionId: proficiency.TemplateRevisionId,
                    Type: proficiency.Type,
                    Name: proficiency.Name,
                    Weight: proficiency.Weight,
                    IsNegative: proficiency.IsNegative,
                    Notes: proficiency.Notes ?? ''
                };
                await new ProficiencyClient().Update(cp);
                await onChange();
            }

        } catch (error) {
            setError(error, ProficiencyErrors.Update);
        }
        finally {
            setBusy(false);
            setOpen(false);
        }
    };

    const handleInputChange = e => {
        const { name, value } = e.target;
        if (name === 'Type') {
            let shouldShow = value === 'Behavior';
            setShowIsNegative(shouldShow);
            // If we are now showing the control, let's uncheck the box.
            if (shouldShow) {
                setProficiency({ ...proficiency, [name]: value, IsNegative: false });
            }
            else {

                setProficiency({ ...proficiency, [name]: value });
            }
        }
        else {
            setProficiency({ ...proficiency, [name]: value });
        }
    };

    const handleCheckBoxChange = e => {
        const { name, checked } = e.target
        setProficiency({ ...proficiency, [name]: checked })
    }

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const loadData = async () => {
        try {
            setBusy(true);
            
            // First load proficiency groups
            const pg = await new ProficiencyGroupClient().GetAll();
            setProficiencyGroups(pg);
            
            // Then handle proficiency data
            if (mode === UpsertAction.Create) {
                // For create, just initialize an empty proficiency
                setProficiency(new Proficiency());
            }
            else if (mode === UpsertAction.Update && proficiencyId) {
                // For update, load the proficiency data
                const p = await new ProficiencyClient().GetSingle(proficiencyId);
                
                // Ensure we wait until both data pieces are loaded before updating UI
                // Use a setTimeout to ensure React state updates have been processed
                setTimeout(() => {
                    const pType = p.Type?.toString() === 'Behavior';
                    setShowIsNegative(pType);
                    setProficiency(p);
                }, 0);
            }
        } catch (error) {
            setError(error, ProficiencyErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title()} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog aria-labelledby="proficiency-upsert-title" open={open} fullWidth tabletFullScreen="portrait" PaperProps={{ sx: { overflow: 'hidden' } }}>
            <ResponsiveDialogHeader id="proficiency-upsert-title" title={title()} onClose={() => setOpen(false)} sx={{ margin: 0, padding: theme.spacing(2) }} />
            <DialogContent dividers sx={{ padding: theme.spacing(2), overflowY: 'auto' }}>
                <BusySpinner busy={busy}>
                    <MobileFormStack tabletColumns={2} desktopColumns={1}>
                        <TextField fullWidth
                            name="Name"
                            label={I18N.Translate('Name')}
                            disabled={mode === UpsertAction.View}
                            sx={{ marginBottom: theme.spacing(1) }}
                            error={IsEmpty(proficiency?.Name)}
                            size='small'
                            variant="outlined"
                            inputRef={input => input && input.focus()}
                            value={proficiency?.Name}
                            onChange={handleInputChange} />
                        {IfTrue(showProficiencyGroupSelect, () =>

                            <TextField fullWidth
                                select
                                label={I18N.Translate('Proficiency Group')}
                                id="profGroup-select"
                                name="ProficiencyGroupId"
                                error={IsEmpty(proficiency?.ProficiencyGroupId)}
                                disabled={mode === UpsertAction.View}
                                value={proficiency?.ProficiencyGroupId || ''}
                                onChange={handleInputChange}>
                                {proficiencyGroups.map((pg) => (
                                    <MenuItem key={pg.Id} value={pg.Id}>{pg.Name}</MenuItem>
                                ))}
                            </TextField>
                        )}

                        <TextField fullWidth select
                            label={I18N.Translate('Proficiency Type')}
                            id="profType-select"
                            name="Type"
                            error={IsEmpty(proficiency?.Type)}
                            disabled={mode === UpsertAction.View}
                            value={proficiency?.Type ?? ''}
                            onChange={handleInputChange}>
                            <MenuItem value="Ability"><I18NText>Ability</I18NText></MenuItem>
                            <MenuItem value="Behavior"><I18NText>Behavior</I18NText></MenuItem>
                        </TextField>

                        {IfTrue(showIsNegative, () =>
                            <Typography>{I18N.Translate("Is Negative")}<Checkbox name={I18N.Translate("IsNegative")} disabled={mode === UpsertAction.View} checked={proficiency?.IsNegative} onChange={handleCheckBoxChange}
                                inputProps={{ 'aria-label': 'primary checkbox' }} /></Typography>
                        )}

                        {IfTrue(showAddToBankCheckbox && mode === UpsertAction.Create, () =>
                            <Typography>{I18N.Translate("Add to Template and Bank")}<Checkbox name={I18N.Translate("AddToBank")} disabled={mode === UpsertAction.View} checked={addToBank} onChange={(e) => onAddToBankChange(e.target.checked)}
                                inputProps={{ 'aria-label': 'primary checkbox' }} /></Typography>
                        )}
                    </MobileFormStack>
                </BusySpinner>
            </DialogContent>
            <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                <Button onClick={() => setOpen(false)} variant='text' color='inherit'><I18NText>{mode === UpsertAction.View ? 'Close' : 'Cancel'}</I18NText></Button>
                <Button disabled={mode === UpsertAction.View || disableSave} onClick={handleSubmit} variant='outlined' color='primary'><I18NText>Submit</I18NText></Button>
            </StickyDialogActions>
        </ResponsiveDialog>
    </>;
}

export default ProficiencyUpsertPopup;
